import { useAuth } from '@clerk/clerk-react';
import { useCallback, useMemo } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  is_read: boolean;
  created_at: string;
}

interface NotificationsResponse {
  success: boolean;
  data?: {
    notifications: Notification[];
    unread_count: number;
    pagination: {
      has_more: boolean;
      next_cursor: string | null;
    };
  };
  error?: string;
}

interface CountResponse {
  success: boolean;
  data?: {
    unread_count: number;
  };
  error?: string;
}

interface ActionResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export function useNotificationsApi() {
  const { getToken } = useAuth();

  const fetchWithAuth = useCallback(
    async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      const token = await getToken();
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...options.headers,
        },
      });
      return response.json() as Promise<T>;
    },
    [getToken]
  );

  return useMemo(
    () => ({
      /**
       * Get user's notifications
       */
      getNotifications: (params?: { unreadOnly?: boolean; limit?: number; cursor?: string }) => {
        const searchParams = new URLSearchParams();
        if (params?.unreadOnly) searchParams.set('unread_only', 'true');
        if (params?.limit) searchParams.set('limit', params.limit.toString());
        if (params?.cursor) searchParams.set('cursor', params.cursor);

        const query = searchParams.toString();
        return fetchWithAuth<NotificationsResponse>(`/notifications${query ? `?${query}` : ''}`);
      },

      /**
       * Get unread count only (lightweight)
       */
      getUnreadCount: () => fetchWithAuth<CountResponse>('/notifications/count'),

      /**
       * Mark a notification as read
       */
      markAsRead: (notificationId: string) =>
        fetchWithAuth<ActionResponse>(`/notifications/${notificationId}/read`, { method: 'PATCH' }),

      /**
       * Mark all notifications as read
       */
      markAllAsRead: () =>
        fetchWithAuth<ActionResponse>('/notifications/read-all', { method: 'PATCH' }),

      /**
       * Delete a notification
       */
      deleteNotification: (notificationId: string) =>
        fetchWithAuth<ActionResponse>(`/notifications/${notificationId}`, { method: 'DELETE' }),
    }),
    [fetchWithAuth]
  );
}
